/**
 * Every cached answer that is built from the shopper's taste profile.
 *
 * WHY THIS LIST EXISTS. Editing the profile is the moment personalization is
 * supposed to become visible — it is the whole reason the screen has a Save
 * button. The backend already does its half: `add`/`remove favorite-brand`,
 * survey submit, taste submit and taste matching all schedule
 * `invalidate_personalization_cache`, so the API serves the new profile on its
 * next uncached request.
 *
 * The client did not. `my-style` mutations invalidated `['my-style']` and
 * `['available-brands']` — the profile screens themselves — while the taste
 * shelf, the segmented home block and the Personalized badge kept their own
 * `staleTime: 5 * 60 * 1000` copies. So a shopper who removed a house from her
 * favourites saw it keep ranking, and read that as personalization ignoring
 * her; `staleTime` only bounds refetch on mount and focus, so on a page she
 * does not leave, it never refreshes at all.
 *
 * ONE LIST, NOT A LINE PER MUTATION. A key added here reaches every profile
 * mutation at once, and a mutation added later cannot remember the shelf and
 * forget the badge — the same reason `personalization/invalidation.py` clears
 * both of its caches in one place rather than at three call sites.
 */

import type { QueryKey } from '@/queries/core/types'

/** Query-key heads whose payload is a function of the taste profile. */
const TASTE_DEPENDENT_HEADS: readonly string[] = [
  // GET /v1/search/you-may-like — ranked by the profile outright
  'search-v1',
  // /goods/popular-today (For You), /goods/popular, may-like, promoted, banners
  'recommendations',
  // GET /v1/personalization/status — the badge's own score
  'personalization',
]

/**
 * Matches every cached query the profile feeds.
 *
 * Head-only on purpose: the keys carry a tab, a size and a cursor, and a
 * matcher that had to enumerate them would go stale the first time one of
 * those gained a member.
 */
// typed as the PREDICATE arm, not as `QueryMatcher`: that is a union with a
// bare key, so a caller composing it (`tasteDependentQueries(key) || ...`)
// could not call it.
export const tasteDependentQueries = (key: QueryKey): boolean =>
  Array.isArray(key) && typeof key[0] === 'string'
    ? TASTE_DEPENDENT_HEADS.includes(key[0])
    : false
